import dayjs from 'dayjs'
import { upperFirst, zip } from 'lodash'

import { GraphBucket, GraphCompareResponse, GraphData, GraphStackResponse } from 'src/api/gen/api'
import { downloadCsv, getHeadersFromJsonList, JsonListToCsv, GraphDataToCsv } from 'src/commons/export'
import { getTranslation } from 'src/commons/i18n'

import { GranularityEnum, IJson, ILink, INode } from '../types'
import { getDocCountSum, getDayJSFormat } from '../utils'

export interface IOutput {
  Date?: string
  Ratio?: number
  count?: number
  countVehicleDeployed?: number
  deployedVehicles?: number
  district?: string
  key?: string | number
  minutesSinceLastTrip?: number
  peak?: number
  timeSinceLastTripInDay?: number
  tripCount?: number
  value?: number
}

export type Translator = (k: string) => string

export function exportDataAsCSV(data: IJson[], filename: string) {
  const headers = getHeadersFromJsonList(data)
  const csvContent = JsonListToCsv(data, headers)
  downloadCsv(csvContent, filename)
}

function withSuffix(radical: string, suffix?: string) {
  return suffix ? `${radical} ${suffix}` : radical
}

export function spreadBy(
  buckets: GraphBucket[],
  suffix?: string,
  only_show_raw = true,
  t: Translator = (k: string) => k
) {
  if (only_show_raw) {
    return buckets.reduce(
      (acc: any, b) => ({
        ...acc,
        [withSuffix(upperFirst(t(b.key)), suffix)]: b.doc_count,
      }),
      {}
    )
  } else {
    return buckets.reduce(
      (acc: any, b) => ({
        ...acc,
        [withSuffix(upperFirst(t(b.key)), `%`)]: b.doc_count,
        [withSuffix(upperFirst(t(b.key)), suffix)]: b.doc_count_without_percentage,
      }),
      {}
    )
  }
}

function getDateFormat(granularity: GranularityEnum) {
  const { t } = getTranslation()
  switch (granularity) {
    case GranularityEnum.day:
      return t('date_format_export.day')
    case GranularityEnum.hour:
      return t('date_format_export.hour')
    case GranularityEnum.month:
      return t('date_format_export.month')
    case GranularityEnum.week:
      return t('date_format_export.week')
    case GranularityEnum.quarter:
      return t('date_format_export.quarter')
  }
}

export function formatDateExport(dateKey: string, granularity: GranularityEnum) {
  return dayjs(dateKey, getDayJSFormat(granularity)).utc(true).format(getDateFormat(granularity))
}

export function exportData(filename: string, data: any = [], formatData: (item: any) => any, download = true) {
  try {
    // sometimes the api returns a GraphCompareWithTotalResponse or GraphBucketWithTotalResponse
    // so the data array is not what is given to the DashboardGraphWrapper
    const dataArray = Array.isArray(data) ? data : data.data
    const jsonList = dataArray.map(formatData)
    const headers = getHeadersFromJsonList(jsonList)
    const csvContent = JsonListToCsv(jsonList, headers)
    if (download) {
      downloadCsv(csvContent, filename)
    } else {
      return { headers, jsonList }
    }
  } catch (err) {
    console.error(err)
  }
}

export function exportGraphData(granularity: GranularityEnum, download = true) {
  const { t } = getTranslation()
  return (filename: string, data: GraphData) => {
    try {
      const headers = [t('kpi.export.Date')].concat(data.datasets ? data.datasets.map((item) => item.label) : [])
      const rows = zip(
        ...[
          data.labels.map((item) => formatDateExport(item, granularity)),
          ...data.datasets.map((items) => items.data.map((item) => item.toString())),
        ]
      )
      const csvContent = GraphDataToCsv(rows, headers)
      if (download) {
        downloadCsv(csvContent, filename)
      } else {
        return { headers, rows }
      }
    } catch (err) {
      console.error(err)
    }
  }
}

// Graph OriginDestinationSankeyComponent
export function exportOriginDestination() {
  return ({ links, nodes }: { links: ILink[]; nodes: INode[] }) => {
    const { t } = getTranslation()
    const originNodes = nodes.filter((n) => n.key.startsWith('origin'))
    const properties: { [key: string]: string | number }[] = originNodes.map((node) => ({
      [t('kpi.export.OriginDestination')]: node.name,
    }))

    links.forEach((link: ILink) => {
      const sourceNode = nodes[link.source]
      const targetNode = nodes[link.target]
      const propId = originNodes.map((n) => n.key).indexOf(sourceNode.key)
      properties[propId] = {
        ...properties[propId],
        [targetNode.name]: link.value,
      }
    })

    return properties
  }
}

// Graphs TripsPerDayComponent and TotalDistance
export function formatExportComparing(granularity: GranularityEnum) {
  const { t } = getTranslation()
  return (bucket: GraphCompareResponse) => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key, granularity),
    ...spreadBy(bucket.compareBy.buckets),
    [t('kpi.export.AverageForAllProviders')]: bucket.average,
  })
}

export function formatExportTotal(granularity: GranularityEnum, title: string) {
  const { t } = getTranslation()
  return (bucket: GraphCompareResponse) => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key, granularity),
    [title]: bucket.doc_count,
  })
}

// Graphs TripsDuration
export function exportTripsPerformed(download = true) {
  const { t } = getTranslation()
  return function (filename: string, data: GraphStackResponse[]) {
    const jsonList = data.map((bucket) => {
      return {
        [t('kpi.export.TripsPerformed')]: bucket.key,
        ...spreadBy(bucket.stackedBy.buckets),
        [t('kpi.export.TotalUsers')]: getDocCountSum(bucket.stackedBy.buckets),
      }
    })

    if (download) {
      exportDataAsCSV(jsonList, filename)
    }
    return jsonList
  }
}

// Graph TripsPerVehicleComponent
export function formatExportTripsPerVehicleRatio(granularity: GranularityEnum) {
  const { t } = getTranslation()
  return (bucket: GraphCompareResponse) => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key, granularity),
    ...spreadBy(bucket.compareBy.buckets),
    [t('kpi.export.AverageForAllProviders')]: bucket.average,
  })
}

export function formatExportTripsPerVehicle(granularity: GranularityEnum) {
  const { t } = getTranslation()
  return (bucket: GraphStackResponse) => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key, granularity),
    [t('kpi.export.NumberOfVehiclesDeployed')]: bucket.stackedBy.buckets[1].doc_count,
    [t('kpi.export.NumberOfTrips')]: bucket.stackedBy.buckets[0].doc_count,
  })
}

// Component UtilizationRateComponent
export function formatExportUtilizationRateComparing(granularity: GranularityEnum) {
  const { t } = getTranslation()
  return (bucket: GraphCompareResponse): IOutput => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key, granularity),
    ...spreadBy(bucket.compareBy.buckets, '(%)', true),
    [t('kpi.export.AverageForAllProviders') + ' (%) ']: bucket.average,
  })
}

export function formatExportUtilizationRate(granularity: GranularityEnum) {
  const { t } = getTranslation()
  return (bucket: GraphBucket) => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key, granularity),
    [t('kpi.export.Ratio') + ' (%)']: bucket.doc_count,
  })
}

// Component TechnicalComplianceComponent
export function formatExportLatency(percentMode: boolean) {
  const { t } = getTranslation()
  return (bucket: GraphBucket) => ({
    [t('kpi.export.Latency')]: bucket.key,
    [`${t('kpi.export.TotalEvents')}${percentMode ? ' (%)' : ''}`]: bucket.doc_count,
  })
}

export function formatExportFleetDeployedArea(granularity: GranularityEnum) {
  const { t } = getTranslation()
  return (bucket: GraphStackResponse) => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key.replace(',', ' '), granularity),
    ...spreadBy(bucket.stackedBy.buckets),
  })
}

export function formatExportVehiclesByProvider() {
  const { t } = getTranslation()
  return (bucket: GraphStackResponse) => ({
    [t('kpi.export.Provider')]: bucket.key,
    ...spreadBy(bucket.stackedBy.buckets),
  })
}

export function formatExportVehiclesByReason(granularity: GranularityEnum) {
  const { t } = getTranslation()
  return (bucket: GraphStackResponse) => ({
    [t('kpi.export.Date')]: formatDateExport(bucket.key, granularity),
    ...spreadBy(bucket.stackedBy.buckets),
  })
}
